import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AuthError } from "@auth/client";
import { animated, config, useSpring } from "@react-spring/web";
import React from "react";
import { useAuth } from "../../hooks/useAuth";
import { PasskeySignInButton } from "../PasskeySignInButton";
// Animated components with proper TypeScript types
const AnimatedForm = animated("form");
const AnimatedDiv = animated("div");
const AnimatedButton = animated("button");
// Modern color palette
const colors = {
    primary: {
        light: "#60a5fa",
        main: "#3b82f6",
        dark: "#2563eb",
    },
    error: {
        light: "#fca5a5",
        main: "#ef4444",
        dark: "#dc2626",
    },
    text: {
        primary: "#111827",
        secondary: "#6b7280",
    },
};
/**
 * LoginForm コンポーネント
 *
 * ユーザー名とパスワードを入力してログインするためのフォームコンポーネント。
 * React 19、Tailwind CSS v4、React Spring を使用した美しくエレガントなUIを提供します。
 *
 * @param props - LoginFormProps
 * @param props.onSuccess - ログイン成功時のコールバック
 * @param props.onError - ログイン失敗時のコールバック
 * @param props.className - カスタムCSSクラス
 */
export function LoginForm({ onSuccess, onError, className }) {
    const { login } = useAuth();
    const [userName, setUserName] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [showPassword, setShowPassword] = React.useState(false);
    // フォームアニメーション
    const formSpring = useSpring({
        from: { opacity: 0, transform: "translateY(-20px)" },
        to: { opacity: 1, transform: "translateY(0px)" },
        config: config.gentle,
    });
    // エラーアニメーション（シェイク）
    const [errorShake, setErrorShake] = React.useState(false);
    React.useEffect(() => {
        if (error) {
            setErrorShake(true);
            const timer = setTimeout(() => setErrorShake(false), 500);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [error]);
    // 送信中のボタンアニメーション
    const buttonSpring = useSpring({
        transform: isSubmitting ? "scale(0.95)" : "scale(1)",
        config: config.wobbly,
    });
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        try {
            const response = await login({ user_name: userName, password });
            onSuccess?.(response);
        }
        catch (err) {
            if (err instanceof AuthError) {
                setError(err.message);
                onError?.(err);
            }
            else if (err instanceof Error) {
                setError(err.message);
                onError?.(new AuthError("INTERNAL_001", err.message));
            }
            else {
                const fallbackError = new AuthError("INTERNAL_001", "ログインに失敗しました");
                setError(fallbackError.message);
                onError?.(fallbackError);
            }
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const formStyle = {
        // 幅と中央寄せはルート要素が持つ。ここで maxWidth / margin:auto を重ねると
        // 親の配置と二重にかかって位置が読めなくなる。
        width: "100%",
        padding: "48px",
        background: "linear-gradient(to bottom right, #ffffff, #f8fafc)",
        borderRadius: "24px",
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)",
        border: "1px solid rgba(226, 232, 240, 0.8)",
    };
    // **単一のルート要素で返すこと。** 以前はフラグメントで AnimatedForm と
    // PasskeySignInButton の 2 要素を返していたため、利用側の親が
    // `display: flex`（CMS admin の `flex items-center justify-center` など）だと
    // 2 つが**横並び**になり、passkey ボタンが中央・入力欄が左へ寄る崩れが出ていた
    // (2026-10-07 報告)。縦並びのコンテナで包んで 1 つの flex child にする。
    const rootStyle = {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        maxWidth: "420px",
    };
    return (_jsxs("div", { style: rootStyle, "data-testid": "login-form-root", children: [_jsxs(AnimatedForm, { onSubmit: handleSubmit, style: { ...formSpring, ...formStyle }, className: className, children: [_jsxs("div", { style: { marginBottom: "40px", textAlign: "center" }, children: [_jsx("div", { style: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "80px",
                                    height: "80px",
                                    marginBottom: "24px",
                                    borderRadius: "20px",
                                    background: `linear-gradient(135deg, ${colors.primary.light}, ${colors.primary.dark})`,
                                    boxShadow: `0 12px 28px ${colors.primary.main}40`,
                                }, children: _jsx("svg", { "aria-hidden": "true", style: { width: "40px", height: "40px", color: "white" }, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2.5, d: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" }) }) }), _jsx("h2", { style: {
                                    fontSize: "32px",
                                    fontWeight: "700",
                                    background: `linear-gradient(135deg, ${colors.text.primary}, ${colors.text.secondary})`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                    marginBottom: "12px",
                                    letterSpacing: "-0.5px",
                                }, children: "\u30ED\u30B0\u30A4\u30F3" }), _jsx("p", { style: {
                                    color: colors.text.secondary,
                                    fontSize: "15px",
                                    fontWeight: "500",
                                }, children: "\u30A2\u30AB\u30A6\u30F3\u30C8\u60C5\u5831\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044" })] }), _jsxs("div", { style: { marginBottom: "24px" }, children: [_jsx("label", { htmlFor: "username", style: {
                                    display: "block",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: colors.text.primary,
                                    marginBottom: "8px",
                                }, children: "\u30E6\u30FC\u30B6\u30FC\u540D" }), _jsx("input", { id: "username", name: "username", type: "text", value: userName, onChange: (e) => setUserName(e.target.value), disabled: isSubmitting, required: true, minLength: 3, maxLength: 50, style: {
                                    width: "100%",
                                    padding: "14px 16px",
                                    fontSize: "15px",
                                    borderRadius: "12px",
                                    border: "2px solid #e2e8f0",
                                    backgroundColor: "#ffffff",
                                    color: colors.text.primary,
                                    transition: "all 0.2s ease",
                                    outline: "none",
                                    opacity: isSubmitting ? 0.6 : 1,
                                    cursor: isSubmitting ? "not-allowed" : "text",
                                }, placeholder: "\u30E6\u30FC\u30B6\u30FC\u540D\u3092\u5165\u529B", onFocus: (e) => {
                                    e.target.style.borderColor = colors.primary.main;
                                    e.target.style.boxShadow = `0 0 0 3px ${colors.primary.main}20`;
                                }, onBlur: (e) => {
                                    e.target.style.borderColor = "#e2e8f0";
                                    e.target.style.boxShadow = "none";
                                } })] }), _jsxs("div", { style: { marginBottom: "24px", position: "relative" }, children: [_jsx("label", { htmlFor: "password", style: {
                                    display: "block",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: colors.text.primary,
                                    marginBottom: "8px",
                                }, children: "\u30D1\u30B9\u30EF\u30FC\u30C9" }), _jsxs("div", { style: { position: "relative" }, children: [_jsx("input", { id: "password", name: "password", type: showPassword ? "text" : "password", value: password, onChange: (e) => setPassword(e.target.value), disabled: isSubmitting, required: true, minLength: 8, maxLength: 128, style: {
                                            width: "100%",
                                            padding: "14px 16px",
                                            paddingRight: "48px",
                                            fontSize: "15px",
                                            borderRadius: "12px",
                                            border: "2px solid #e2e8f0",
                                            backgroundColor: "#ffffff",
                                            color: colors.text.primary,
                                            transition: "all 0.2s ease",
                                            outline: "none",
                                            opacity: isSubmitting ? 0.6 : 1,
                                            cursor: isSubmitting ? "not-allowed" : "text",
                                        }, placeholder: "\u30D1\u30B9\u30EF\u30FC\u30C9\u3092\u5165\u529B", onFocus: (e) => {
                                            e.target.style.borderColor = colors.primary.main;
                                            e.target.style.boxShadow = `0 0 0 3px ${colors.primary.main}20`;
                                        }, onBlur: (e) => {
                                            e.target.style.borderColor = "#e2e8f0";
                                            e.target.style.boxShadow = "none";
                                        } }), _jsx("button", { type: "button", onClick: () => setShowPassword(!showPassword), style: {
                                            position: "absolute",
                                            right: "12px",
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            color: colors.text.secondary,
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "4px",
                                            transition: "color 0.2s ease",
                                        }, "aria-label": showPassword ? "パスワードを隠す" : "パスワードを表示", onMouseEnter: (e) => {
                                            e.currentTarget.style.color = colors.text.primary;
                                        }, onMouseLeave: (e) => {
                                            e.currentTarget.style.color = colors.text.secondary;
                                        }, children: showPassword ? (_jsxs("svg", { "aria-hidden": "true", style: { width: "20px", height: "20px" }, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: [_jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M15 12a3 3 0 11-6 0 3 3 0 016 0z" }), _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" })] })) : (_jsx("svg", { "aria-hidden": "true", style: { width: "20px", height: "20px" }, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" }) })) })] })] }), error && (_jsx(AnimatedDiv, { role: "alert", style: {
                            marginBottom: "24px",
                            padding: "14px 16px",
                            borderRadius: "12px",
                            backgroundColor: "#fef2f2",
                            border: `2px solid ${colors.error.light}`,
                            color: colors.error.dark,
                            animation: errorShake ? "shake 0.5s ease-in-out" : "none",
                        }, children: _jsxs("div", { style: { display: "flex", alignItems: "center" }, children: [_jsx("svg", { "aria-hidden": "true", style: { width: "20px", height: "20px", marginRight: "8px", flexShrink: 0 }, fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z", clipRule: "evenodd" }) }), _jsx("span", { style: { fontWeight: "500", fontSize: "14px" }, children: error })] }) })), _jsx(AnimatedButton, { type: "submit", disabled: isSubmitting, style: {
                            ...buttonSpring,
                            width: "100%",
                            padding: "16px 24px",
                            borderRadius: "12px",
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#ffffff",
                            background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.primary.dark})`,
                            border: "none",
                            cursor: isSubmitting ? "not-allowed" : "pointer",
                            opacity: isSubmitting ? 0.7 : 1,
                            boxShadow: isSubmitting
                                ? "0 8px 24px rgba(59, 130, 246, 0.25)"
                                : "0 12px 32px rgba(59, 130, 246, 0.35)",
                            transition: "all 0.2s ease",
                        }, onMouseEnter: (e) => {
                            if (!isSubmitting) {
                                e.currentTarget.style.boxShadow = "0 16px 40px rgba(59, 130, 246, 0.45)";
                                e.currentTarget.style.transform = "translateY(-2px)";
                            }
                        }, onMouseLeave: (e) => {
                            if (!isSubmitting) {
                                e.currentTarget.style.boxShadow = "0 12px 32px rgba(59, 130, 246, 0.35)";
                                e.currentTarget.style.transform = "translateY(0)";
                            }
                        }, children: isSubmitting ? (_jsxs("span", { style: { display: "flex", alignItems: "center", justifyContent: "center" }, children: [_jsxs("svg", { "aria-hidden": "true", style: {
                                        width: "20px",
                                        height: "20px",
                                        marginRight: "12px",
                                        animation: "spin 1s linear infinite",
                                    }, fill: "none", viewBox: "0 0 24 24", children: [_jsx("circle", { style: { opacity: 0.25 }, cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { style: { opacity: 0.75 }, fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }), "\u30ED\u30B0\u30A4\u30F3\u4E2D..."] })) : ("ログイン") }), _jsx("div", { style: { marginTop: "24px", textAlign: "center" }, children: _jsx("p", { style: { fontSize: "13px", color: colors.text.secondary, fontWeight: "500" }, children: "\u30D1\u30B9\u30EF\u30FC\u30C9\u3092\u304A\u5FD8\u308C\u3067\u3059\u304B\uFF1F" }) }), _jsx("style", { children: `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
      ` })] }), _jsx(PasskeySignInButton, { ...(onSuccess !== undefined ? { onSuccess } : {}), ...(onError !== undefined
                    ? {
                        onError: (err) => {
                            if (err instanceof AuthError) {
                                onError(err);
                            }
                        },
                    }
                    : {}) })] }));
}
